import { GetProgramByLandingPagePathQuery_programForLandingPage_onboardingTemplates } from '@frontend/app/queries/types/GetProgramByLandingPagePathQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { CatalogOrderRequestInput, ContentUsageRightsType, FieldType, ProgramOnboardingTemplateType, TermsProjectInput, TermsType } from '@frontend/app/types/globalTypes';
import { GET_MEMBER_JOIN_PAGE } from '@frontend/app/queries/GetMemberJoinPageQuery';
import { GetMemberJoinPageQueryVariables } from '@frontend/app/queries/types/GetMemberJoinPageQuery';
import { GetMemberJoinPageQuery } from '@frontend/app/queries/types/GetMemberJoinPageQuery';
import { useGetPublicTermsSettingsForProgram, useParsedRouterSearch } from '@frontend/app/hooks';
import { useMutation, useQuery } from '@apollo/client';
import { chain, filter, find, includes, map, reduce, uniqBy } from 'lodash';
import { localToUTC } from '@frontend/app/utils';
import { addDays, startOfDay } from 'date-fns';
import { MemberJoinProgramMutation, MemberJoinProgramMutationVariables } from '@frontend/app/queries/types/MemberJoinProgramMutation';
import { MEMBER_JOIN_PROGRAM } from '@frontend/app/queries';
import { GetPublicTermsSettingsForProgramQuery_termsTemplate } from '@frontend/app/queries/types/GetPublicTermsSettingsForProgramQuery';
import { mapDueDates } from '@frontend/applications/TermsApp/utils/TermsMappings';
import { useGetPublicContentGuidelinesById } from '@frontend/app/hooks/useGetPublicContentGuidelinesById';
import { GetPublicContentGuidelineById_guideline } from '@frontend/app/queries/types/GetPublicContentGuidelineById';
import { DateOperator } from '@frontend/applications/TermsApp/components/BulkTerms/types/ContentDateRequirements';
import { message } from '@revfluence/fresh';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FormValues, useRecruitment } from './RecruitmentContext';
import { ViewMode } from './constants';
import { PreviewBody } from './Components/PreviewBody';
import { IField } from '../../LandingPages/types';

const INSTAGRAM_FIELD = 'Instagram';
const SOCIAL_FIELDS_WITH_HANDLER = [INSTAGRAM_FIELD, 'TikTok', 'Pinterest', 'Twitter'];

const convertTermsTemplateToTermsProjectInput = (
  termsTemplate: GetPublicTermsSettingsForProgramQuery_termsTemplate,
  clientId: string,
  programId: number,
  contentGuidelineArg?: GetPublicContentGuidelineById_guideline,
): TermsProjectInput => {
  const contentGuideline = {
    accountHandle: '',
    guidelines: map(contentGuidelineArg?.guidelines || [], (g) => ({
      ...g,
      __typename: undefined,
    })),
    label: contentGuidelineArg?.label,
    id: contentGuidelineArg?.id,
    postType: contentGuidelineArg?.type,
    networkType: contentGuidelineArg?.networkType,
    requiresContentReview: false,
    attachments: map(contentGuidelineArg?.attachments || [], (attachment) => ({
        filename: attachment.filename,
        url: attachment.url,
        type: attachment.type,
      })),
  };
  const contentGuidelines = termsTemplate.contentRequirements.areDueDatesSelected
  ? [{
    ...contentGuideline,
    count: termsTemplate.contentRequirements.deliverablesDueDates.length,
    dueDates: mapDueDates({
      completeDate: map(termsTemplate.contentRequirements.deliverablesDueDates, (deliverableDueDate) => ({
        operator: DateOperator.ON,
        onDate: addDays(new Date(), deliverableDueDate),
        dueDateInDays: deliverableDueDate,
      })),
      submissionDate: [],
    }),
    acceptedPostTypes: termsTemplate.contentRequirements.contentTypes,
    requiresBrandedContentTag: false,
    requiresContentReview: false,
  }]
  : [{
    ...contentGuideline,
    count: termsTemplate.contentRequirements.deliverablesWithNoDueDates,
    dueDates: null,
    acceptedPostTypes: termsTemplate.contentRequirements.contentTypes,
    requiresBrandedContentTag: false,
    requiresContentReview: false,
  }];

  return {
    introMessage: termsTemplate.introMessage,
    // Include emailDetails but with empty values if skipEmailDetails is true
    emailDetails: {
      subject: '',
      body: '',
      resourceId: -1,
      attachments: [],
      additionalCc: [],
    },
    advertiserAccess: {
      startDate: '',
      endDate: '',
      requireAccess: false,
    },
    brandedContentAccess: {
      requireAccess: false,
    },
    clientId,
    programId,
    agreementIteration: {
      paymentTerms: '',
      briefUrl: '',
      commissionDescription: termsTemplate.compensationCommissionValue,
      productDescription: termsTemplate.compensationFreeProductValue,
      contentUsageRights: termsTemplate.contentUsageRightValue,
      disableCreatorEdits: true,
      offersPayment: termsTemplate.compensationPaymentAvailable && termsTemplate.compensationPaymentValue > 0,
      offersProduct: termsTemplate.compensationFreeProductAvailable && !!termsTemplate.compensationFreeProductValue,
      offersCommission: termsTemplate.compensationCommissionAvailable && !!termsTemplate.compensationCommissionValue,
      price: termsTemplate.compensationPaymentValue || 0,
      requireContentRights: termsTemplate.contentUsageRightValue !== ContentUsageRightsType.NONE,
      requireInstagramInsights: false,
      contentGuidelines,
      requireIgBcPromoteAccess: false,
      requireIgPartnershipAdsAccess: false,
      requireMetaBamAccess: false,
      igBcPromoteAccessEndDate: '',
      igBcPromoteAccessStartDate: '',
      metaBamAccessEndDate: '',
      metaBamAccessStartDate: '',
      requireTiktokSparkAdsAccess: false,
      tiktokSparkAdsDuration: null,
    },
  };
};

const convertAdvancedTermsTemplateToTermsProjectInput = (
  termsTemplate: GetPublicTermsSettingsForProgramQuery_termsTemplate,
  clientId: string,
  programId: number,
): TermsProjectInput => {
  return {
    emailDetails: {
      subject: '',
      body: '',
      resourceId: -1,
      attachments: [],
      additionalCc: [],
    },
    advertiserAccess: {
      startDate: '',
      endDate: '',
      requireAccess: false,
    },
    brandedContentAccess: {
      requireAccess: false,
    },
    clientId,
    programId,
    agreementIteration: {
      requireIgBcPromoteAccess: false,
      igBcPromoteAccessEndDate: '',
      igBcPromoteAccessStartDate: '',
      requireMetaBamAccess: false,
      metaBamAccessEndDate: '',
      metaBamAccessStartDate: '',
      requireIgPartnershipAdsAccess: false,
      requireTiktokSparkAdsAccess: false,
      tiktokSparkAdsDuration: null,
      paymentTerms: '',
      briefUrl: '',
      commissionDescription: termsTemplate.compensationCommissionValue,
      productDescription: termsTemplate.compensationFreeProductValue,
      contentUsageRights: termsTemplate.contentUsageRightValue,
      disableCreatorEdits: false,
      offersPayment: termsTemplate.compensationPaymentAvailable && termsTemplate.compensationPaymentValue > 0,
      offersProduct: termsTemplate.compensationFreeProductAvailable && !!termsTemplate.compensationFreeProductValue,
      offersCommission: termsTemplate.compensationCommissionAvailable && !!termsTemplate.compensationCommissionValue,
      price: termsTemplate.compensationPaymentValue || 0,
      requireContentRights: termsTemplate.contentUsageRightValue !== ContentUsageRightsType.NONE,
      requireInstagramInsights: false,
      contentGuidelines: [],
    }
  };
};

interface RecruitmentCreatorViewProps {
  projectId: number;
  projectTitle: string;
  template: GetProgramByLandingPagePathQuery_programForLandingPage_onboardingTemplates;
}
export const RecruitmentCreatorView = ({ projectId, projectTitle, template }: RecruitmentCreatorViewProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>(window.innerWidth > 768 ? ViewMode.DESKTOP : ViewMode.MOBILE);

  const [firstRender, setFirstRender] = useState(true);

  const { initAmplifyTemplate, initPulseTemplate, amplifyTemplate, pulseTemplate } = useRecruitment();

  const { pathname, search } = useLocation();

  const history = useHistory();

  const { url } = useRouteMatch();

  useEffect(() => {
    if (firstRender) {
      const currentPageUrl = pathname.split('/').pop();
      if (['brief', 'product', 'form', 'confirmation'].includes(currentPageUrl)) {
        history.replace(`${url}${search}`);
      }
    }
    setFirstRender(false);
  }, [firstRender, history, pathname, search, url]);

  const {
    clientId,
    memberId,
    utmSource,
    token,
  } = useParsedRouterSearch();

  const context = useMemo(() => (
    {
      headers: {
        'requested-client-id': clientId,
      },
    }
  ), [clientId]);

  const {
    data: {
      data = null,
    } = {},
  } = useQuery<GetMemberJoinPageQuery, GetMemberJoinPageQueryVariables>(
    GET_MEMBER_JOIN_PAGE,
    {
      variables: {
        customLandingPagePath: encodeURIComponent(template.pageUrl),
      },
      context,
    },
  );

  const { data: termsSettings } = useGetPublicTermsSettingsForProgram({
    variables: {
      programId: projectId,
    },
    skip: !projectId,
    context,
  });

  const { contentGuideline } = useGetPublicContentGuidelinesById({
    variables: {
      id: termsSettings?.termsTemplate?.contentGuidelineId,
    },
    skip: !termsSettings?.termsTemplate?.contentGuidelineId,
  });

  const [submitMember, { loading: isSubmitting }] = useMutation<MemberJoinProgramMutation, MemberJoinProgramMutationVariables>(
    MEMBER_JOIN_PROGRAM,
    {
      onError: (error) => {
        message.error(error.message || 'Something went wrong, please try again.');
      },
    },
  );

  useEffect(() => {
    if (template) {
      if (template.templateType === ProgramOnboardingTemplateType.PULSE) {
        const form = template.config.pulseTemplate.pages[0].hero;
        const extendedForm = template.config.pulseTemplate.pages.find((page) => !!page.forms)?.forms;
        const formFields = data?.fields?.filter((f) => {
          return form?.applicationFormFields?.memberFieldSchemas?.some((field) => field.schemaId === f.id)
          || form?.applicationFormFields?.dbColumns?.some((field) => field.name === f.name);
        });
        const extendedFormFields = data?.fields?.filter((f) => {
          return extendedForm?.applicationFormFields?.memberFieldSchemas?.some((field) => field.schemaId === f.id)
          || extendedForm?.applicationFormFields?.dbColumns?.some((field) => field.name === f.name);
        });
        initPulseTemplate(template, (formFields || []) as unknown as IField[], (extendedFormFields || []) as unknown as IField[]);
      } else if (template.templateType === ProgramOnboardingTemplateType.AMPLIFY) {
        const form = template.config.amplifyTemplate.pages[0].home?.forms;
        const extendedForm = template.config.amplifyTemplate.pages.find((page) => !!page.forms)?.forms;
        const formFields = data?.fields?.filter((f) => {
          return form?.applicationFormFields?.memberFieldSchemas?.some((field) => field.schemaId === f.id)
          || form?.applicationFormFields?.dbColumns?.some((field) => field.name === f.name);
        });
        const extendedFormFields = data?.fields?.filter((f) => {
          return extendedForm?.applicationFormFields?.memberFieldSchemas?.some((field) => field.schemaId === f.id)
          || extendedForm?.applicationFormFields?.dbColumns?.some((field) => field.name === f.name);
        });
        initAmplifyTemplate(template, (formFields || []) as unknown as IField[], (extendedFormFields || []) as unknown as IField[]);
      }
    }
  }, [initPulseTemplate, initAmplifyTemplate, template, data?.fields]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setViewMode(ViewMode.DESKTOP);
      } else {
        setViewMode(ViewMode.MOBILE);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sanitizeSocialValue = (value) => chain(value)
      .toLower()
      .replace('@', '')
      .trim()
      .value();

  const handleSubmit = async (values: FormValues, catalogOrderRequest?: CatalogOrderRequestInput) => {
    const form = (
      amplifyTemplate?.pages?.[0]?.home?.form
      || pulseTemplate?.pages?.[0]?.hero
    );
    const extendedForm = (
      amplifyTemplate?.pages?.find((page) => !!page.form)?.form
      || pulseTemplate?.pages?.find((page) => !!page.form)?.form
    );

    const allFormFields = uniqBy([
      ...(form?.fields || []),
      ...(extendedForm?.fields || []),
    ], (f) => f.id);

    const memberFields = allFormFields;

    const hasBriefPage = !!(
      amplifyTemplate?.pages?.some((page) => !!page.brief)
      || pulseTemplate?.pages?.some((page) => !!page.brief)
    );

    const isAnnualDateField = (id) => {
      const annualDateField = memberFields.find((field) => field.id === parseInt(id, 10))?.type;
      return annualDateField === FieldType.ANNUAL || annualDateField === FieldType.DATE;
    };

    const getFieldIdByName = (name: string) => {
      const field = find(memberFields, (field) => field?.name === name);

      if (field?.id && field.id > 0) {
        return field.id.toString();
      }

      return name;
    };

    const socialFieldIds = map(
        filter(memberFields, (field) => includes(SOCIAL_FIELDS_WITH_HANDLER, field?.name)),
        (field) => field.id.toString(),
      );

    const isSocialField = (id) => includes(socialFieldIds, id);

    const sanitizedValues = reduce(values, (obj, value, nameOrColumnId) => {
      const columnId = getFieldIdByName(nameOrColumnId);

      if (isSocialField(columnId)) {
        obj[columnId] = sanitizeSocialValue(value);
      } else if (isAnnualDateField(columnId)) {
        obj[columnId] = localToUTC(startOfDay(value as Date));
      } else {
        obj[columnId] = value;
      }

      return obj;
    }, {});

    await submitMember({
      variables: {
        customLandingPagePath: encodeURIComponent(template.pageUrl),
        fields: sanitizedValues,
        memberId,
        token,
        utmSource,
        briefTemplate: (hasBriefPage && termsSettings?.termsTemplate) ? (
          termsSettings.termsTemplate.termsType === TermsType.ADVANCED
            ? convertAdvancedTermsTemplateToTermsProjectInput(termsSettings.termsTemplate, clientId, projectId)
            : convertTermsTemplateToTermsProjectInput(termsSettings.termsTemplate, clientId, projectId, contentGuideline)
        ) : undefined,
        catalogOrderRequest,
      },
      context,
    });
  };

  return (
    <PreviewBody programTitle={projectTitle} viewMode={viewMode} termsSettings={termsSettings?.termsTemplate} contentGuideline={contentGuideline} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
  );
};
